import { Parser } from 'htmlparser2';

// Mirrors API security/contentLimits.ts. A streaming parser keeps Unicode,
// entity, structure and raw image-byte accounting identical across platforms.
export const CONTENT_LIMITS = Object.freeze({ about: 190, aboutLines: 4, title: 1200, comment: 60000, htmlBytes: 512 * 1024, photos: 10, nodes: 20000, depth: 64 });
const blocks = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote', 'pre']);
const invisible = new Set(['script', 'style', 'template', 'noscript', 'svg', 'math', 'iframe', 'object', 'img', 'audio', 'video', 'textarea', 'option']);
export function codePointCount(value) {
  let count = 0;
  for (const character of String(value ?? '')) { void character; count += 1; }
  return count;
}

export function measureAbout(about) {
  const text = String(about ?? '').replace(/\r\n?|[\u2028\u2029]/g, '\n');
  const characters = codePointCount(text);
  const lines = text.split('\n').length;
  return { text, characters, lines, error: characters > CONTENT_LIMITS.about ? 'ABOUT_LIMIT_EXCEEDED'
    : lines > CONTENT_LIMITS.aboutLines ? 'ABOUT_LINE_LIMIT_EXCEEDED' : null };
}

export function measureTitle(title) {
  const characters = codePointCount(title);
  let hasHtml = false;
  new Parser({ onopentagname() { hasHtml = true; }, onclosetag() { hasHtml = true; },
    oncomment() { hasHtml = true; }, onprocessinginstruction() { hasHtml = true; } }, { decodeEntities: false }).end(title);
  return { characters, error: characters > CONTENT_LIMITS.title ? 'TITLE_LIMIT_EXCEEDED'
    : hasHtml ? 'TITLE_HTML_NOT_ALLOWED' : null };
}

function rawImagePayloadBytes(attribute, decodedValue) {
  const assignment = /^[^\s=]+\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'`=<>]+))/.exec(attribute);
  if (!assignment) return 0;
  const rawValue = assignment[1] ?? assignment[2] ?? assignment[3];
  if (rawValue !== decodedValue) return 0;
  const prefix = /^data:image\/(?:png|jpe?g|gif|webp|avif);base64,/i.exec(rawValue);
  if (!prefix) return 0;
  const payload = rawValue.slice(prefix[0].length);
  if (!payload.length || payload.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(payload)) return 0;
  return payload.length; // Base64 is ASCII, so bytes and code units are identical.
}

export function measureComment(html) {
  const result = { characters: 0, htmlBytes: new Blob([html]).size, photos: 0, nodes: 0, depth: 0 };
  const stack = [];
  let hiddenDepth = 0;
  let structureExceeded = false;
  let lastStructuralNewline = false;
  let lastWasCR = false;
  let previousText = false;
  let openingTag = '';
  let sawSource = false;
  let imagePayloadBytes = 0;
  const node = () => {
    result.nodes++;
    if (result.nodes > CONTENT_LIMITS.nodes) structureExceeded = true;
  };
  const lineBreak = (structural) => {
    result.characters++;
    lastStructuralNewline = structural;
    lastWasCR = false;
  };
  let parser;
  parser = new Parser({
    onopentagname(name) { openingTag = name; sawSource = false; imagePayloadBytes = 0; },
    onattribute(name, value) {
      if (openingTag !== 'img' || name !== 'src' || sawSource) return;
      sawSource = true;
      imagePayloadBytes = rawImagePayloadBytes(html.slice(parser.startIndex, parser.endIndex + 1), value);
    },
    onopentag(name, attrs) {
      previousText = false;
      if (name === 'img') result.htmlBytes -= imagePayloadBytes;
      node();
      if (stack.length + 1 > CONTENT_LIMITS.depth) structureExceeded = true;
      result.depth = Math.max(result.depth, stack.length + 1);
      const marker = (name === 'div' && attrs['data-wiw-quote'] === 'true')
        || (name === 'span' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(attrs['data-recorded-media-id'] || ''));
      // Photos inside a quoted/hidden subtree belong to neither the author nor their quota.
      if (!hiddenDepth && !marker && name === 'img') result.photos++;
      const hidden = invisible.has(name) || marker;
      stack.push({ name, hidden });
      if (hidden) hiddenDepth++;
      if (!hiddenDepth && name === 'br') lineBreak(false);
    },
    ontext(value) {
      // htmlparser2 may deliver each entity separately; DOM has one text node.
      if (!previousText) node();
      previousText = true;
      if (hiddenDepth) return;
      for (const character of value) {
        if (character === '\n' && lastWasCR) { lastWasCR = false; continue; }
        result.characters++;
        lastWasCR = character === '\r';
        lastStructuralNewline = false;
      }
    },
    onclosetag(name) {
      previousText = false;
      if (!hiddenDepth && blocks.has(name)) lineBreak(true);
      const frame = stack.pop();
      if (frame?.hidden) hiddenDepth--;
    },
    oncomment() { previousText = false; node(); },
    onprocessinginstruction() { previousText = false; node(); },
  }, { decodeEntities: true });
  parser.end(html);
  if (lastStructuralNewline) result.characters--;
  const error = structureExceeded ? 'COMMENT_STRUCTURE_LIMIT_EXCEEDED'
    : result.characters > CONTENT_LIMITS.comment ? 'COMMENT_TEXT_LIMIT_EXCEEDED'
    : result.htmlBytes > CONTENT_LIMITS.htmlBytes ? 'COMMENT_HTML_LIMIT_EXCEEDED'
    : result.photos > CONTENT_LIMITS.photos ? 'COMMENT_PHOTO_LIMIT_EXCEEDED' : null;
  return { ...result, error };
}

export function contentLimitMessage(error, t) {
  const messages = {
    ABOUT_LIMIT_EXCEEDED: ['aboutLimitExceeded', 'Shorten your description to 190 characters before saving. Your text is still here.'],
    ABOUT_LINE_LIMIT_EXCEEDED: ['aboutLineLimitExceeded', 'Keep your description to 4 lines before saving. Your text is still here.'],
    TITLE_LIMIT_EXCEEDED: ['titleLimitExceeded', 'The event title can contain up to 1,200 characters. Your text is still here.'],
    TITLE_HTML_NOT_ALLOWED: ['titleHTMLNotAllowed', 'Use plain text for the event title. Your text is still here.'],
    COMMENT_TEXT_LIMIT_EXCEEDED: ['commentLimitExceeded', 'A comment can contain up to 60,000 characters. Your full draft is still here.'],
    COMMENT_HTML_LIMIT_EXCEEDED: ['commentMarkupLimitExceeded', 'There is too much formatting. Simplify it before sending. Your full draft is still here.'],
    COMMENT_PHOTO_LIMIT_EXCEEDED: ['commentPhotoLimitExceeded', 'A comment can contain up to 10 photos. Remove extra photos before sending.'],
    COMMENT_STRUCTURE_LIMIT_EXCEEDED: ['commentMarkupLimitExceeded', 'There is too much formatting. Simplify it before sending. Your full draft is still here.'],
  };
  const message = messages[error];
  return message ? t(`brand:${message[0]}`, { defaultValue: message[1] }) : null;
}
